import { gql, useMutation } from '@apollo/client';
import {
  Button,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
} from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import React, { useCallback } from 'react';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const asyncRerunCheckWorkflowMutation = gql<
  GraphQLTypes.RerunCheckWorkflowAsyncMutation,
  GraphQLTypes.RerunCheckWorkflowAsyncMutationVariables
>`
  mutation RerunCheckWorkflowAsyncMutation(
    $graphId: ID!
    $checkWorkflowId: ID!
  ) {
    service(id: $graphId) {
      checkWorkflow(id: $checkWorkflowId) {
        id
        rerunAsync {
          ... on CheckRequestSuccess {
            targetURL
            workflowID
          }
          ... on InvalidInputError {
            message
          }
          ... on PermissionError {
            message
          }
          ... on PlanError {
            message
          }
        }
      }
    }
  }
`;

export const SummaryRerunButtonWorkflowFragment = gql`
  fragment SummaryRerunButtonWorkflowFragment on CheckWorkflow {
    id
    upstreamWorkflow {
      id
    }
  }
`;

interface Props {
  trigger: React.ReactElement;
  graphId: string;
  workflow: GraphQLTypes.SummaryRerunButtonWorkflowFragment;
}

export function SummaryRerunButton({ trigger, graphId, workflow }: Props) {
  const checkWorkflowId = workflow.upstreamWorkflow?.id ?? workflow.id;
  const [rerunCheck, { loading, error }] = useMutation(
    asyncRerunCheckWorkflowMutation,
    {
      variables: {
        graphId,
        checkWorkflowId,
      },
      update: (cache, { data }, { variables }) => {
        const result = data?.service?.checkWorkflow?.rerunAsync;
        if (result?.__typename === 'CheckRequestSuccess') {
          cache.modify({
            id: cache.identify({
              __typename: 'CheckWorkflow',
              id: variables?.checkWorkflowId,
            }),
            fields: {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'reruns({"limit":1})': (_cachedServices, { toReference }) => {
                const newServiceRef = toReference(
                  { __typename: 'CheckWorkflow', id: result.workflowID },
                  true,
                );
                return [newServiceRef];
              },
            },
          });
        }
      },
      onCompleted: (data) => {
        const result = data.service?.checkWorkflow?.rerunAsync;
        if (
          result?.__typename === 'InvalidInputError' ||
          result?.__typename === 'PermissionError' ||
          result?.__typename === 'PlanError'
        ) {
          Sentry.captureMessage(result.message, { level: 'error' });
        }
      },
    },
  );

  const reRunCallback = useCallback(() => {
    return rerunCheck();
  }, [rerunCheck]);

  return (
    <PermissionWarning graphId={graphId} graphPermissions="canCheckSchemas">
      <Popover autoFocus={false} placement="bottom-start" size="sm">
        <PopoverTrigger>{trigger}</PopoverTrigger>
        <div className="z-50">
          <PopoverContent>
            <PopoverHeader>
              Rerun this check
              <PopoverCloseButton />
            </PopoverHeader>
            <PopoverBody>
              This will also update the status of your check on Github. Note:
              when using other CI integrations, CI status may become out of
              sync.
              {error && <ErrorMessage className="my-4" />}
            </PopoverBody>
            <PopoverFooter>
              <Button
                variant="primary"
                onClick={reRunCallback}
                isLoading={loading}
                loadingText="Rerunning check"
              >
                Rerun check
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </div>
      </Popover>
    </PermissionWarning>
  );
}
